import { useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ExternalLink, Search } from 'lucide-react'
import { DataTableBlock, type ListPageColumn } from '@/components/templates/list-page'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { Input } from '@/components/ui/input'
import { getJSON } from '@/lib/api'
import { displayShortDate } from '@/lib/dates'

type LearningRow = {
  course_title: string
  status: 'enrolled' | 'completed'
  enrolled_on: string | null
  completed_on: string | null
  grade_label: string | null
  credits_earned: string | null
}
export type MyLearning = {
  learning_link?: string | null
  enrolments: LearningRow[]
  completed_credits: string
  credit_unit_label: string
}

const statuses = [
  { value: 'completed', label: 'Completed' },
  { value: 'enrolled', label: 'In progress' },
]

export function MemberPortalMyLearning() {
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  // The resource accepts one status; both (like neither) requests the complete transcript.
  const status = selected.size === 1 ? [...selected][0] : ''
  const query = useQuery({
    queryKey: ['member-portal-my-learning', search, status],
    queryFn: () => getJSON<{ my_learning: MyLearning }>(learningPath(search, status)),
    retry: false,
    placeholderData: keepPreviousData,
  })

  return (
    <div className="mt-6 space-y-6">
      <div data-testid="learning-record-slot" hidden={!query.data?.my_learning.learning_link}>
        {query.data?.my_learning.learning_link && (
          <Button asChild variant="outline">
            <a
              href={query.data.my_learning.learning_link}
              target="_blank"
              rel="noopener noreferrer"
            >
              View my learning record{' '}
              <ExternalLink className="ml-1.5 size-3.5" aria-hidden="true" />
            </a>
          </Button>
        )}
      </div>
      {query.data && <LearningTotal learning={query.data.my_learning} />}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            className="h-9 pl-9"
            aria-label="Search courses"
            placeholder="Search courses…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <FilterDropdown
          label="Status"
          emptyLabel="Status"
          options={statuses}
          selected={selected}
          onChange={setSelected}
        />
      </div>
      <LearningContent
        loading={query.isLoading}
        error={query.isError}
        learning={query.data?.my_learning}
      />
    </div>
  )
}

function learningPath(search: string, status: string) {
  const params = new URLSearchParams()
  if (search) params.set('search', search)
  if (status) params.set('status', status)
  const query = params.toString()
  return `/api/v1/member_portal/my_learning${query ? `?${query}` : ''}`
}

function LearningTotal({ learning }: { learning: MyLearning }) {
  return (
    <dl className="space-y-1">
      <dt className="text-caption text-muted-foreground">{learning.credit_unit_label} earned</dt>
      <dd className="text-body font-semibold tabular-nums">{learning.completed_credits}</dd>
    </dl>
  )
}

function LearningContent({
  loading,
  error,
  learning,
}: {
  loading: boolean
  error: boolean
  learning?: MyLearning
}) {
  if (loading)
    return (
      <p role="status" className="text-body-sm text-muted-foreground">
        Loading learning…
      </p>
    )
  if (error || !learning) return <Alert>Learning is unavailable. Please try again.</Alert>
  if (learning.enrolments.length === 0)
    return <EmptyState title="No courses yet." variant="quiet" />

  return (
    <section className="overflow-hidden rounded-md border border-border bg-card [&_table]:min-w-[600px]">
      <DataTableBlock
        table={{
          rows: learning.enrolments.map((row, index) => ({ ...row, rowKey: index })),
          columns: learningColumns(learning.credit_unit_label),
          getRowId: (row) => row.rowKey,
          getRowLabel: (row) => row.course_title,
          footer: (
            <div className="flex justify-end border-t border-border px-4 py-2 text-body-sm">
              Total ·{' '}
              <span className="ml-1 font-semibold tabular-nums">
                {learning.completed_credits} {learning.credit_unit_label}
              </span>
            </div>
          ),
        }}
      />
    </section>
  )
}

function learningColumns(unit: string): ListPageColumn<LearningRow>[] {
  return [
    {
      id: 'course',
      header: 'Course',
      cell: (row) => row.course_title,
      className: 'font-medium whitespace-nowrap',
    },
    { id: 'status', header: 'Status', cell: (row) => <LearningStatus status={row.status} /> },
    {
      id: 'enrolled',
      header: 'Enrolled',
      cell: (row) => row.enrolled_on && displayShortDate(row.enrolled_on),
      className: 'whitespace-nowrap text-muted-foreground',
    },
    {
      id: 'completed',
      header: 'Completed',
      cell: (row) => row.completed_on && displayShortDate(row.completed_on),
      className: 'whitespace-nowrap text-muted-foreground',
    },
    { id: 'grade', header: 'Grade', cell: (row) => row.grade_label },
    {
      id: 'credits',
      header: unit,
      cell: (row) => row.credits_earned,
      className: 'text-right tabular-nums',
      headerClassName: 'text-right whitespace-nowrap',
    },
  ]
}

function LearningStatus({ status }: { status: LearningRow['status'] }) {
  if (status === 'completed')
    return (
      <Badge variant="success" className="whitespace-nowrap">
        Completed
      </Badge>
    )
  if (status === 'enrolled')
    return (
      <Badge variant="info" className="whitespace-nowrap">
        In progress
      </Badge>
    )
  return null
}
